import { EventDesignFragment } from '@graphql/generated';
import { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { createContext, useContext } from 'react';

export interface DeleteComponentDetails
  extends Readonly<{
    cancelCta: string;
    deleteCta: string;
    errorMessage: string;
    id: string;
    successMessage: string;
    subTitle: string;
    title: string;
    type?: string;
  }> {}

export interface BlockOrderData
  extends Readonly<{
    blockOrdering: ReadonlyArray<string>;
    id: string;
    sortOrder: number;
  }> {}

export type CustomComponentStateBlockBaseProps = {};

/**
 * While navigate screens(list, add, edit) in custom page editor left panel, we need to handle state between each screen
 * customComponentBlockData - Handled state for CustomComponents like TravelMap, Travel, Photo etc, Also handled state for CustomComponents blocks like RoomBlock, Hotel, Custom etc.
 * blockOrderData - To persist sort order state of nested items of active CustomComponent
 */
export interface CustomComponentStateContextProps<T extends CustomComponentStateBlockBaseProps>
  extends Readonly<{
    blockOrderData?: BlockOrderData;
    customComponentBlockData?: T;
    deleteComponentDetails?: DeleteComponentDetails;
    eventDesign?: EventDesignFragment;
    matchedHotel?: HotelInventory | null;
    refetchQueries: ReadonlyArray<string>;
    selectedHotelPhotos?: { url: string }[] | null;
    showDeletePopup: boolean;
    setBlockOrderData: (blockOrderData?: BlockOrderData) => void;
    setCustomComponentBlockData: (data: T) => void;
    setDeleteComponentDetails: (deleteDialogDetails?: DeleteComponentDetails) => void;
    setMatchedHotel: (hotel: HotelInventory | null) => void;
    setRefetchQueries: (refetchQueries: ReadonlyArray<string>) => void;
    setSelectedHotelPhotos: (hotelPhotos: ReadonlyArray<{ url: string }> | null) => void;
    setShowDeletePopup: (showDeletePopup: boolean) => void;
  }> {}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CustomComponentStateContext = createContext<CustomComponentStateContextProps<any> | undefined>(undefined);

export const useCustomComponentStateContext = <T extends CustomComponentStateBlockBaseProps>(): CustomComponentStateContextProps<T> => {
  const context = useContext(CustomComponentStateContext) as CustomComponentStateContextProps<T> | undefined;
  if (!context) {
    throw new Error('useCustomComponentStateContext must be used within an CustomComponentStateProvider');
  }
  return context;
};
